import {Map as IMap} from 'immutable';
import {weakMemo} from '@packages/utilities/memo';
import {defer, force} from '@packages/utilities/lazy';
import {
  Component,
  Expressions,
  Plugins,
  type Type,
  type EnvironmentContextType,
  withSystem,
} from '@packages/systems/wfdl';
import {
  type Expression,
  type ExpressionElement,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {tyfun} from '@packages/utilities/wfdl-helpers';
import {
  getName,
  qualify,
  type Qualified,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  getComponentRootElement,
  getElementWithAncestors,
  isUserComponentInst,
  pageComponentType,
} from '@packages/utilities/core-elements';
import {isProjectingElement} from '@packages/utilities/elementTypeUtils';
import {
  computeNativeId,
  getGuid,
  getInstanceIndicesFromNativeId,
  type NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {CMS_RICH_TEXT_OVERLAY_TARGET_SCOPE} from './constants';
import type {EnclosingRichText} from './richTextScope';

export type OverlayTargetResolver = (
  instanceIndexSuffix: string | undefined,
  nativeIdPath: readonly NativeId[],
  triggerElementId?: string,
  enclosingRichText?: EnclosingRichText
) => string | null;
export type OverlayTarget = string | OverlayTargetResolver;

/**
 * What tells the overlay kinds apart when resolving their targets: which
 * elements are targets, how a target's DOM id is formed, and the Link value
 * that a `context.<kind>Target(componentKey)(targetId)(action)` call
 * evaluates to.
 */
export type OverlayKindConfig<Factory> = {
  isTarget: (element: ExpressionElement) => boolean;
  isTargetType: (type: ExpressionElement['type']) => boolean;
  getDomId: (runtimeNativeIdPath: readonly NativeId[]) => string;
  createTargetFactory: (target: OverlayTarget) => Factory;
  fallbackTargetFactory: Factory;
};

const pageComponentKey = 'page';

const countAncestorProjectingElements = (
  elementId: string,
  tree: Expression
): number =>
  getElementWithAncestors(tree)(elementId).filter(isProjectingElement).length;

const takeSuffixPrefix = (
  instanceIndexSuffix: string | undefined,
  depth: number
): string | undefined => {
  if (typeof instanceIndexSuffix !== 'string' || depth === 0) {
    return undefined;
  }

  return instanceIndexSuffix.split('-').slice(0, depth).join('-');
};

const getTargetDomId = (
  getDomId: OverlayKindConfig<unknown>['getDomId'],
  targetPath: readonly string[],
  innerProjectionDepth: number,
  instanceIndexSuffix: string | undefined,
  nativeIdPath: readonly NativeId[]
): string => {
  const lastNativeId = nativeIdPath[nativeIdPath.length - 1];
  const outerProjectionDepth = lastNativeId
    ? getInstanceIndicesFromNativeId(lastNativeId).length
    : 0;
  const targetInstanceIndexSuffix = takeSuffixPrefix(
    instanceIndexSuffix,
    outerProjectionDepth + innerProjectionDepth
  );
  const environmentContext: EnvironmentContextType = IMap(
    targetInstanceIndexSuffix
      ? {instanceIndexSuffix: targetInstanceIndexSuffix}
      : {}
  );

  return getDomId([
    ...nativeIdPath,
    ...targetPath.map((elementId) =>
      computeNativeId(elementId, environmentContext)
    ),
  ]);
};

const getOwnerNativeIdPath = (
  rootComponent: Qualified<Component.ComponentName>,
  ownerComponentKey: string,
  nativeIdPath: readonly NativeId[],
  components: Component.ComponentMap
): readonly NativeId[] | null => {
  if (getName(rootComponent) === ownerComponentKey) return [];

  let currentComponent = components.get(rootComponent);
  for (let index = 0; index < nativeIdPath.length; index += 1) {
    const nativeId = nativeIdPath[index];
    if (!nativeId || !currentComponent) return null;
    const componentInstance = Expressions.getElementById(
      Component.getRender(currentComponent),
      getGuid(nativeId)
    );
    if (
      !componentInstance ||
      !isUserComponentInst(components, componentInstance)
    ) {
      return null;
    }

    if (getName(componentInstance.type) === ownerComponentKey) {
      return nativeIdPath.slice(0, index + 1);
    }
    currentComponent = components.get(componentInstance.type);
  }

  return null;
};

const matchesDescendantPath = (
  component: Component.ComponentType,
  descendantNativeIds: readonly NativeId[],
  triggerElementId: string,
  components: Component.ComponentMap
): boolean => {
  let currentComponent = component;

  for (const nativeId of descendantNativeIds) {
    const componentInstance = Expressions.getElementById(
      Component.getRender(currentComponent),
      getGuid(nativeId)
    );
    if (
      !componentInstance ||
      !isUserComponentInst(components, componentInstance)
    ) {
      return false;
    }

    const nextComponent = components.get(componentInstance.type);
    if (!nextComponent) return false;
    currentComponent = nextComponent;
  }

  return Boolean(
    Expressions.getElementById(
      Component.getRender(currentComponent),
      triggerElementId
    )
  );
};

const getOwnerNativeIdPathFromDescendant = (
  ownerComponent: Component.ComponentType,
  nativeIdPath: readonly NativeId[],
  triggerElementId: string | undefined,
  components: Component.ComponentMap
): readonly NativeId[] | null => {
  if (!triggerElementId) return null;

  const matchingBoundaries: number[] = [];
  for (let boundary = 0; boundary <= nativeIdPath.length; boundary += 1) {
    const descendantNativeIds = nativeIdPath.slice(boundary);
    if (
      matchesDescendantPath(
        ownerComponent,
        descendantNativeIds,
        triggerElementId,
        components
      )
    ) {
      matchingBoundaries.push(boundary);
    }
  }

  return matchingBoundaries.length === 1
    ? nativeIdPath.slice(0, matchingBoundaries[0])
    : null;
};

const buildTarget = (
  getDomId: OverlayKindConfig<unknown>['getDomId'],
  targetPath: readonly string[],
  innerProjectionDepth: number,
  ownerComponentKey: string,
  ownerComponent: Component.ComponentType,
  rootComponent: Qualified<Component.ComponentName>,
  components: Component.ComponentMap
): OverlayTarget => {
  if (
    getName(rootComponent) === pageComponentKey &&
    ownerComponentKey === pageComponentKey &&
    innerProjectionDepth === 0
  ) {
    return getTargetDomId(
      getDomId,
      targetPath,
      innerProjectionDepth,
      undefined,
      []
    );
  }

  return (instanceIndexSuffix, nativeIdPath, triggerElementId) => {
    const ownerNativeIdPath =
      getOwnerNativeIdPath(
        rootComponent,
        ownerComponentKey,
        nativeIdPath,
        components
      ) ??
      getOwnerNativeIdPathFromDescendant(
        ownerComponent,
        nativeIdPath,
        triggerElementId,
        components
      );
    if (!ownerNativeIdPath) return null;

    return getTargetDomId(
      getDomId,
      targetPath,
      innerProjectionDepth,
      instanceIndexSuffix,
      ownerNativeIdPath
    );
  };
};

const getOverlayTargetPath = (
  {isTarget}: OverlayKindConfig<unknown>,
  element: ExpressionElement,
  components: Component.ComponentMap
): string[] | null => {
  if (isTarget(element)) return [element.id];
  if (!isUserComponentInst(components, element)) return null;

  const root = getComponentRootElement(components, element.type);
  return root && isTarget(root) ? [element.id, root.id] : null;
};

const getOverlayTargetAccessor = <Factory>(
  config: OverlayKindConfig<Factory>,
  componentKey: string,
  component: Component.ComponentType,
  components: Component.ComponentMap,
  rootComponent: Qualified<Component.ComponentName>
): ((targetId: string) => Factory) => {
  const targetFactories = new Map<string, Factory>();
  const {fallbackTargetFactory} = config;
  let elements: Record<string, ExpressionElement> | undefined;

  return (targetId) => {
    const existingFactory = targetFactories.get(targetId);
    if (existingFactory) return existingFactory;

    const render = Component.getRender(component);
    if (!elements) {
      elements = Expressions.allElements(render);
    }
    const element = elements[targetId];
    if (!element) return fallbackTargetFactory;

    const targetPath = getOverlayTargetPath(config, element, components);
    if (!targetPath) return fallbackTargetFactory;

    const innerProjectionDepth = countAncestorProjectingElements(
      element.id,
      render
    );
    const target = buildTarget(
      config.getDomId,
      targetPath,
      innerProjectionDepth,
      componentKey,
      component,
      rootComponent,
      components
    );
    const targetFactory = config.createTargetFactory(target);
    targetFactories.set(targetId, targetFactory);

    return targetFactory;
  };
};

/**
 * Rich text content (e.g. a CMS RTF value) is not in the component map, so its
 * targets come from the enclosing rich text: a target renders at the rich
 * text's own position, with the rich text's instance suffix.
 */
const getCmsRichTextTargetDomId = (
  {isTarget, isTargetType, getDomId}: OverlayKindConfig<unknown>,
  targetId: string,
  {nativeIdPath, instanceIndexSuffix, targets}: EnclosingRichText,
  components: Component.ComponentMap
): string | null => {
  const type = targets.get(targetId);
  if (!type) return null;

  let targetPath: string[];
  if (isTargetType(type)) {
    targetPath = [targetId];
  } else {
    const root = getComponentRootElement(components, type);
    if (!root || !isTarget(root)) return null;
    targetPath = [targetId, root.id];
  }

  const environmentContext: EnvironmentContextType = IMap(
    instanceIndexSuffix === undefined ? {} : {instanceIndexSuffix}
  );

  return getDomId([
    ...nativeIdPath,
    ...targetPath.map((elementId) =>
      computeNativeId(elementId, environmentContext)
    ),
  ]);
};

/**
 * Popover or Modal targets addressed with the
 * `CMS_RICH_TEXT_OVERLAY_TARGET_SCOPE` componentKey
 * are looked up when the trigger renders, in the nearest rich text that
 * encloses it. A target outside that rich text, or one that no longer exists,
 * resolves to no target attribute at all.
 */
const getCmsRichTextTargetAccessor = <Factory>(
  config: OverlayKindConfig<Factory>,
  components: Component.ComponentMap
): ((targetId: string) => Factory) => {
  const targetFactories = new Map<string, Factory>();

  return (targetId) => {
    const existingFactory = targetFactories.get(targetId);
    if (existingFactory) return existingFactory;

    const target: OverlayTargetResolver = (
      _instanceIndexSuffix,
      _nativeIdPath,
      _triggerElementId,
      enclosingRichText
    ) =>
      enclosingRichText
        ? getCmsRichTextTargetDomId(
            config,
            targetId,
            enclosingRichText,
            components
          )
        : null;
    const targetFactory = config.createTargetFactory(target);
    targetFactories.set(targetId, targetFactory);

    return targetFactory;
  };
};

export const OverlayTargetContextType: Type = withSystem(
  {hiddenProperty: true},
  tyfun(
    [TText, TText, TText],
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('Link')))
  )
);

type OverlayTargetContext<Factory> = (
  componentKey: string
) => (targetId: string) => Factory;

type BuildOverlayTargetContext<Factory> = (
  components: Component.ComponentMap,
  rootComponent?: Qualified<Component.ComponentName>
) => OverlayTargetContext<Factory>;

export const defineOverlayTargetContext = <Factory>(
  config: OverlayKindConfig<Factory>
): {
  buildContext: BuildOverlayTargetContext<Factory>;
  createContextRef: () => {
    context: OverlayTargetContext<Factory>;
    update: (
      components: Component.ComponentMap,
      rootComponent?: Qualified<Component.ComponentName>
    ) => void;
  };
} => {
  const fallbackTargetAccessor = () => config.fallbackTargetFactory;

  const buildForRoot = weakMemo((components: Component.ComponentMap) =>
    weakMemo(
      (
        rootComponent: Qualified<Component.ComponentName>
      ): OverlayTargetContext<Factory> => {
        const componentAccessors = new Map<
          string,
          (targetId: string) => Factory
        >();

        components.forEach((component, componentName) => {
          const componentKey = getName(componentName);
          componentAccessors.set(
            componentKey,
            getOverlayTargetAccessor(
              config,
              componentKey,
              component,
              components,
              rootComponent
            )
          );
        });
        const cmsRichTextAccessor = getCmsRichTextTargetAccessor(
          config,
          components
        );

        return (componentKey) =>
          componentKey === CMS_RICH_TEXT_OVERLAY_TARGET_SCOPE
            ? cmsRichTextAccessor
            : (componentAccessors.get(componentKey) ?? fallbackTargetAccessor);
      }
    )
  );

  const buildContext: BuildOverlayTargetContext<Factory> = (
    components,
    rootComponent = pageComponentType
  ) => buildForRoot(components)(rootComponent);

  const createContextRef = () => {
    let components: Component.ComponentMap | undefined;
    let rootComponent: Qualified<Component.ComponentName> | undefined;
    let lazyContext = defer<OverlayTargetContext<Factory>>(
      () => () => () => config.fallbackTargetFactory
    );
    const context: OverlayTargetContext<Factory> = (componentKey) =>
      force(lazyContext)(componentKey);

    return {
      context,
      update: (
        nextComponents: Component.ComponentMap,
        nextRootComponent: Qualified<Component.ComponentName> = pageComponentType
      ) => {
        if (
          components === nextComponents &&
          rootComponent === nextRootComponent
        ) {
          return;
        }

        lazyContext = defer(() =>
          buildContext(nextComponents, nextRootComponent)
        );
        components = nextComponents;
        rootComponent = nextRootComponent;
      },
    };
  };

  return {buildContext, createContextRef};
};
